import React, {
	createContext,
	CSSProperties,
	Fragment,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { Modal, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { GlobalContext, UserContext } from "../../App";
import UsernameAvatarDisplay from "../Insertions/UsernameAvatarDisplay";
import CommentButtons from "../Insertions/CommentButtons";
import { element, ThreadManagerProps } from "./types";
import ReadMore from "../Insertions/ReadMore";
import moment from "moment";
import CommentReaction from "../Insertions/CommentReaction";

import "./style.scss";
import {
	createReply,
	getReplies,
	subscribeToThread,
	toggleThreadLock,
} from "../../api";
import { UserConstants } from "../../constants";

import { toast } from "react-toastify";
import Paginate from "../Pagination/Pagination";

const ThreadContext = createContext<any>({});
const initialState: ThreadManagerProps = {
	document: null,
	setDocuments: null,
	origin: null,
};

const ThreadManager = ({ threadProps }) => {
	const { setThreadProps } = useContext<any>(GlobalContext);
	const { currentUserData } = useContext<any>(UserContext);

	const { document, origin, setDocuments } = threadProps;
	const [lock, setLock] = useState<boolean>(false);
	const [subbed, setSubbed] = useState<boolean>(
		document?.subscribers?.includes(currentUserData?._id),
	);
	const [lockedThread, setLockedThread] = useState<boolean>(document?.locked);
	const [replies, setReplies] = useState<any>([]);
	const [numberOfPages, setNumberOfPages] = useState<number>(0);
	const [fetchedDocId, setFetchedDocId] = useState<string>("");
	const [page, setPage] = useState<number>(1);

	const [showCommentBox, setShowCommentBox] = useState<boolean>(false);
	const [content, setContent] = useState<string>("");
	const [previewState, setPreviewState] = useState<boolean>(false);

	const threadBodyRef = useRef<null | HTMLDivElement>(null);
	const commentBoxRef = useRef<null | HTMLTextAreaElement>(null);

	const userIsMuted = !!currentUserData?.mainRoles.includes(
		UserConstants.Punishments.MUTED,
	);

	const revealLockButton =
		(currentUserData?.isStaff && origin !== "announcement") ||
		currentUserData?.isAdmin;

	const show =
		!!threadProps?.document?._id &&
		!!threadProps?.origin &&
		threadProps?.setDocuments !== null;

	const subTypeMap = {
		announcement: "announcement",
		level: "levelComment",
		profile: "profileComment",
	};

	useEffect(() => {
		setSubbed(document?.subscribers?.includes(currentUserData?._id));
		setLockedThread(document?.locked);
	}, [currentUserData?._id, document?.subscribers, document?.locked]);

	useEffect(() => {
		setShowCommentBox(false);
		setPreviewState(false);
		setContent(
			sessionStorage?.getItem(`replyContent-${document?._id}`) || "",
		);
	}, [show, document?._id]);

	const subscribe = () => {
		const force = document?.subscribers?.includes(currentUserData?._id)
			? "unsubscribe"
			: "subscribe";
		setLock(true);
		subscribeToThread(document._id, force, subTypeMap[origin])
			.then(() => {
				setSubbed((prev) => !prev);
				setDocuments((prev) =>
					prev.map((doc) => {
						if (doc._id !== document._id) return doc;
						// Add or remove user
						const updatedSubscribers =
							force === "subscribe"
								? [
										...(doc?.subscribers || []),
										currentUserData._id,
									] // add
								: doc?.subscribers?.filter(
										(id) => id !== currentUserData._id, // remove
									);
						return {
							...(doc || []),
							subscribers: updatedSubscribers,
						};
					}),
				);
			})
			.finally(() => setLock(false));
	};

	const handleLock = () => {
		setLock(true);
		toggleThreadLock(document._id, origin)
			.then(() => {
				setLockedThread((prev) => !prev);
				setDocuments((prev) =>
					prev.map(
						(d) =>
							d?._id === document._id
								? { ...d, locked: !d.locked } // modified
								: d, // original
					),
				);
			})
			.finally(() => setLock(false));
	};

	const handleCreateReply = () => {
		if (lock) return;
		if (content.length === 0)
			return toast.error("Text, I need more, TEXT!");
		setLock(true);
		createReply(document._id, origin, { content: content })
			.then((res) => {
				const data = res?.data;
				if (data) {
					const pageNum = parseInt(data?.numberOfPages);
					setFetchedDocId(document._id);
					setShowCommentBox(false);
					setPreviewState(false);
					setContent("");
					sessionStorage.removeItem(`replyContent-${document._id}`);
					if (page < pageNum) {
						setNumberOfPages(pageNum);
						setPage(pageNum);
					} else {
						setReplies((prev) => [...prev, data?.reply]);
						if (!numberOfPages) setNumberOfPages(1);
					}
					setDocuments((prev) =>
						prev.map(
							(d) =>
								d?._id === document._id
									? {
											...d, // modified
											replies: [
												...d?.replies,
												data?.reply?._id,
											],
										}
									: d, // original
						),
					);
				}
			})
			.finally(() => setLock(false));
	};

	const iconButtons = [
		{
			tooltip: previewState ? "Turn off preview" : "Preview reply",
			icon: previewState ? "visibility_off" : "visibility",
			colour: previewState ? "rgba(229, 225, 10, 1)" : null,
			onClick: () => {
				setPreviewState((p) => !p);
			},
		},
		{
			tooltip: "Post reply",
			icon: "send",
			colour: "rgba(20, 219, 10, 1)",
			onClick: handleCreateReply,
		},
	];

	const updateReplyContent = (e) => {
		setContent(e.target.value);
		sessionStorage.setItem(`replyContent-${document._id}`, e.target.value);
	};

	const commentBoxEl = showCommentBox ? (
		<div className="commentbox-container unselectable">
			{previewState ? (
				<div className="preview">
					<ReadMore
						content={content}
						document={null}
						height={450}
						rank={currentUserData?.rank}
					/>
				</div>
			) : (
				<textarea
					value={content}
					ref={commentBoxRef}
					onChange={updateReplyContent}
					maxLength={5000}
					className="form-control"
					placeholder="Invoke your writing skills here!"
				/>
			)}
			<div>
				{iconButtons.map((button, index) => (
					<OverlayTrigger
						key={index}
						placement="top"
						overlay={<Tooltip>{button.tooltip}</Tooltip>}>
						<span
							className="notranslate material-icons-outlined blue thread-icon"
							style={{
								...(lock && {
									transform: "unset",
									pointerEvents: "none",
									opacity: 0.5,
								}),
								...(!!button?.colour && {
									color: button.colour,
								}),
							}}
							onClick={button.onClick}>
							{button.icon}
						</span>
					</OverlayTrigger>
				))}
			</div>
		</div>
	) : null;

	const replyButtonEl = (
		<button
			className={
				"btn btn-primary" + (showCommentBox ? "__danger " : "__special")
			}
			style={{
				display:
					(!!currentUserData?.verified &&
						!userIsMuted &&
						document?.visibility !== "deleted") ||
					!!currentUserData?.isAdmin
						? "block"
						: "none",
			}}
			onClick={() => {
				setShowCommentBox((prev) => {
					const current = threadBodyRef?.current;
					if (current && !prev) {
						// scroll
						requestAnimationFrame(() => {
							requestAnimationFrame(() => {
								current.scrollTo({
									top: current.scrollHeight,
									behavior: "smooth",
								});
							});
						});
					}
					return !prev;
				});
			}}
			disabled={lock || (!revealLockButton && lockedThread)}>
			{lockedThread && !revealLockButton
				? "Locked"
				: showCommentBox
					? "Cancel"
					: "Reply"}
		</button>
	);

	return (
		<ThreadContext.Provider
			value={{
				...threadProps,
				replies,
				setReplies,
				numberOfPages,
				setNumberOfPages,
			}}>
			<Modal
				show={show}
				onHide={() => {
					setThreadProps(initialState);
				}}
				aria-labelledby="contained-modal-title-vcenter"
				size={show ? "xl" : "sm"}
				className="popup-modal"
				centered>
				<span className="modal-fill" style={{ position: "relative" }}>
					<Modal.Body
						className="modal-bdy modal-filter"
						ref={threadBodyRef}
						style={{
							scrollBehavior: "smooth",
							position: "relative",
						}}>
						<ThreadBody
							show={show}
							setFetchedDocId={setFetchedDocId}
							fetchedDocId={fetchedDocId}
							page={page}
							setPage={setPage}
						/>
						{commentBoxEl}
					</Modal.Body>
					<Modal.Footer
						className="modal-filter modal-buttons"
						style={{ display: show ? "flex" : "none" }}>
						{replyButtonEl}
						<button
							className="btn btn-primary"
							disabled={lock}
							onClick={subscribe}>
							{subbed ? "Unsubscribe" : "Subscribe"}
						</button>
						<button
							className="btn btn-primary"
							onClick={() => {
								setThreadProps(initialState);
							}}>
							Close
						</button>
						{revealLockButton && (
							<button
								className={
									"btn btn-primary" +
									(lockedThread ? "__special" : "__danger")
								}
								disabled={lock}
								onClick={handleLock}>
								{lockedThread ? "Unlock" : "Lock"}
							</button>
						)}
					</Modal.Footer>
				</span>
			</Modal>
		</ThreadContext.Provider>
	);
};

const ThreadBody = ({ show, setFetchedDocId, fetchedDocId, page, setPage }) => {
	const { currentUserData } = useContext(UserContext);
	const { setThreadProps } = useContext<any>(GlobalContext);
	const { document, origin, setDocuments } = useContext(ThreadContext);

	const [docState, setDocState] = useState<any>([document]);
	const [editContent, setEditContent] = useState<string>(document?.content);

	const currentDoc = docState[0];

	const author = document?.author;

	const visibilityIndex =
		currentDoc?.visibility === "deleted"
			? 2
			: currentDoc?.highlighted
				? 1
				: 0;
	const visibilityColours = {
		0: "255, 255, 255",
		1: "255, 238, 0",
		2: "211, 36, 36",
	};

	const levelOrigin = !!currentDoc?.level;
	const profileOrigin = !!currentDoc?.profile;

	const style = {
		"--border-colour": visibilityColours[visibilityIndex],
	} as CSSProperties;

	useEffect(() => {
		setDocuments((prev) =>
			prev
				.map((d) => {
					// close if deleted and not staff
					if (visibilityIndex === 2 && !currentUserData?.isStaff)
						setThreadProps(initialState);

					// then sync state
					return d._id === currentDoc._id
						? {
								...currentDoc,
								editState: d?.editState,
								previewState: d?.previewState,
							}
						: d;
				})
				.filter((d) =>
					visibilityIndex === 2 &&
					!currentUserData?.isStaff &&
					d?._id === currentDoc._id // remove if it is deleted and the user isnt staff
						? false
						: true,
				),
		);
		// eslint-disable-next-line
	}, [currentDoc]);

	if (!show) return <h1 className=" center-textoutput">Closing...</h1>;

	return (
		<div className={`thread-manager-body`}>
			<div>
				<div className="top-comment hover-colour" style={style}>
					<div>
						<div className="fix-modal-flicker">
							<UsernameAvatarDisplay user={author} />
						</div>
						<CommentButtons
							elementsToDisplay={[
								"edit",
								"delete",
								"obliterate",
								"copy",
								...(levelOrigin || profileOrigin) ? ["pin" as element] : [],
								...levelOrigin ? ["highlight" as element] : [],
							]}
							comment={currentDoc}
							type="comment"
							origin={origin}
							setComments={setDocState}
							thread={false}
							elementProps={{
								editContent,
								parentId:
									document?.level ||
									document?.profile ||
									document?._id,
							}}
						/>
					</div>
					<div
						className="comment-content"
						style={{ whiteSpace: "pre-wrap" }}>
						{currentDoc?.editState && !currentDoc?.previewState ? (
							<textarea
								className="form-control"
								style={{
									width: "100%",
									margin: "unset",
									height: "125px",
								}}
								value={editContent}
								onChange={(e) => setEditContent(e.target.value)}
							/>
						) : (
							<ReadMore
								content={currentDoc?.content}
								document={currentDoc}
								height={450}
								rank={currentDoc?.author?.rank}
							/>
						)}
					</div>
					<div>
						<CommentReaction
							document={currentDoc}
							setDocuments={setDocState}
							type={origin}
						/>
						<div>
							Posted on{" "}
							<b>
								{moment(currentDoc?.postDate).format(`
									MMM D, YYYY - h:mm A
								`)}
							</b>
						</div>
					</div>
				</div>
			</div>
			<ThreadReplies
				fetchedDocId={fetchedDocId}
				setFetchedDocId={setFetchedDocId}
				page={page}
				setPage={setPage}
			/>
		</div>
	);
};

const ThreadReplies = ({ fetchedDocId, setFetchedDocId, page, setPage }) => {
	const {
		document,
		origin,
		replies,
		setReplies,
		numberOfPages,
		setNumberOfPages,
	} = useContext(ThreadContext);
	const [isQuerying, setIsQuerying] = useState<boolean>(false);
	const [prevPage, setPrevPage] = useState<number>(page || -1);

	const differentDocument = document?._id !== fetchedDocId;

	useEffect(() => {
		if ((differentDocument || prevPage !== page) && !isQuerying) {
			const pageToUse = differentDocument ? 1 : page;
			setPage(pageToUse);
			setPrevPage(pageToUse);
			setFetchedDocId(document?._id);
			setIsQuerying(true);
			// fetch the documents
			getReplies(document?._id, origin, { page: pageToUse || 1 })
				.then((res) => {
					const data = res?.data;
					if (data) {
						setReplies(data?.replies);
						setNumberOfPages(data?.numberOfPages ?? 1);
					}
				})
				.finally(() => {
					setIsQuerying(false);
				});
		}
	}, [
		isQuerying,
		differentDocument,
		origin,
		setReplies,
		setFetchedDocId,
		prevPage,
		page,
		setNumberOfPages,
		setPage,
		document?._id,
	]);

	if (isQuerying || document?._id !== fetchedDocId) {
		return (
			<div className="flex-center">
				<Spinner animation="grow" variant="primary" />
			</div>
		);
	}

	if (!replies?.length)
		return (
			<i className="center-textoutput gray">
				This thread appears to be empty
			</i>
		);

	return (
		<>
			<Paginate
				numberOfPages={numberOfPages}
				noparampage={page}
				setPage={setPage}
				type="noparams"
			/>
			{replies?.map((reply) => (
				<Fragment key={reply?._id}>
					<MappedReply reply={reply} />
				</Fragment>
			))}
		</>
	);
};

const MappedReply = ({ reply }) => {
	const { document, origin, setReplies } = useContext(ThreadContext);

	const [editContent, setEditContent] = useState<string>(reply?.content);

	const replyAuthor = reply?.author;

	const statusProps = {
		0: { name: "Deleted", icon: "delete", colour: "rgba(246, 49, 14, 1)" },
		1: { name: "Public", icon: "globe", colour: "rgba(65, 227, 255, 1)" },
	};

	const statusObj = statusProps[reply?.deleted ? 0 : 1];

	return (
		<div
			style={{
				borderColor: reply?.deleted ? "red" : "white",
			}}
			className={`reply hover-colour`}>
			<div>
				<UsernameAvatarDisplay user={replyAuthor} />
				<CommentButtons
					elementsToDisplay={["edit", "delete", "obliterate", "copy"]}
					comment={reply}
					type="reply"
					origin={origin}
					setComments={setReplies}
					thread={false}
					elementProps={{ parentId: document?._id, editContent }}
				/>
			</div>
			<div className="reply-content">
				{reply?.editState && !reply?.previewState ? (
					<textarea
						className="form-control"
						style={{
							width: "100%",
							margin: "unset",
							height: "125px",
						}}
						value={editContent}
						onChange={(e) => setEditContent(e.target.value)}
					/>
				) : (
					<ReadMore
						content={
							reply?.previewState ? editContent : reply?.content
						}
						document={reply}
						rank={replyAuthor?.rank}
					/>
				)}
			</div>
			<div>
				<div
					className="flex-center"
					style={{
						color: statusObj.colour,
						whiteSpace: "nowrap",
						gap: "4px",
					}}>
					<span className="material-icons-round">
						{statusObj.icon}
					</span>
					<span>{statusObj.name}</span>
				</div>
				<div>
					Posted on{" "}
					<b>
						{moment(reply?.postDate).format(`
									MMM D, YYYY - h:mm A
								`)}
					</b>
				</div>
			</div>
		</div>
	);
};

export default ThreadManager;
